import { toJpeg } from 'html-to-image';
import { v4 as uuidV4 } from 'uuid';
import logger from './logger';
import uploadUtils from './uploadUtils';
export const FEEDBACK_UI_ROOT_CLASS = 't-feedback';
export const SCREENSHOT_JPEG_QUALITY = 0.82;
export const SCREENSHOT_PIXEL_RATIO = 1;

// 1x1 transparent GIF. html-to-image assigns this to any image it cannot re-fetch; without it the
// library assigns an empty src, which fires onerror and rejects the whole capture.
export const TRANSPARENT_PIXEL = 'data:image/gif;base64,' + 'R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

/**
 * Feedback chrome must never appear in the capture — including when the panel is already open
 * (retake). Walking closest() covers the root and every descendant.
 */
export const isFeedbackUiNode = node => typeof Element !== 'undefined' && node instanceof Element && typeof node.closest === 'function' && Boolean(node.closest(`.${FEEDBACK_UI_ROOT_CLASS}`));

/**
 * Options for html-to-image.
 *
 * Deliberate choices, both learned from real failures on form pages:
 * - No `cacheBust`: it appends a query param, which invalidates the signature on presigned S3
 *   URLs (signature images, photos) and turns them into 403s.
 * - `imagePlaceholder` + `onImageErrorHandler`: a single unreachable image must degrade to a blank
 *   box, never fail the screenshot.
 *
 * @param {{ scrollX?: number, scrollY?: number }} [scroll={}] Current scroll offset, so the image
 *   shows the part of the page the user is actually looking at.
 */
export const buildCaptureOptions = ({
  scrollX: _scrollX = 0,
  scrollY: _scrollY = 0
} = {}) => ({
  quality: SCREENSHOT_JPEG_QUALITY,
  pixelRatio: SCREENSHOT_PIXEL_RATIO,
  backgroundColor: '#ffffff',
  imagePlaceholder: TRANSPARENT_PIXEL,
  onImageErrorHandler: () => {},
  // Keeps the resource cache keyed per signed URL instead of per path.
  includeQueryParams: true,
  filter: node => !isFeedbackUiNode(node),
  style: {
    transform: `translate(${-_scrollX}px, ${-_scrollY}px)`,
    transformOrigin: 'top left'
  }
});

/**
 * Capture the visible page as a JPEG Blob, excluding the feedback widget itself.
 *
 * @param {Window} [scope=window]
 * @returns {Promise<Blob|null>} JPEG blob, or null when capture is unavailable / fails
 */
export const captureViewportScreenshot = async (scope = window) => {
  const doc = scope && scope.document;
  if (!doc || !doc.documentElement) {
    return null;
  }
  try {
    const dataUrl = await toJpeg(doc.documentElement, buildCaptureOptions({
      scrollX: scope.scrollX || 0,
      scrollY: scope.scrollY || 0
    }));
    const response = await fetch(dataUrl);
    return response.blob();
  } catch (error) {
    logger('Feedback screenshot capture failed', error);
    return null;
  }
};

/**
 * Upload a captured screenshot into the customer's S3 bucket via the shared upload helpers.
 *
 * @param {Blob} blob JPEG (or image) blob from captureViewportScreenshot
 * @returns {Promise<string>} Remote object key stored as screenshotReference
 */
export const uploadFeedbackScreenshot = async blob => {
  if (!blob) {
    throw new Error('Cannot upload feedback screenshot without an image blob');
  }
  const fileName = `feedback-screenshots/${uuidV4()}.jpg`;
  const file = new File([blob], fileName, {
    type: blob.type || 'image/jpeg'
  });
  const result = await uploadUtils.uploadFileToS3(file, fileName);
  if (!(result !== null && result !== void 0 && result.remotePath)) {
    throw new Error('Feedback screenshot upload did not return a remote path');
  }
  return result.remotePath;
};
export default {
  FEEDBACK_UI_ROOT_CLASS,
  TRANSPARENT_PIXEL,
  buildCaptureOptions,
  captureViewportScreenshot,
  isFeedbackUiNode,
  uploadFeedbackScreenshot
};